import React, { useState } from 'react';
import {
  Box, Typography, Stack, Button, Paper, MenuItem, TextField, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { BRAND } from '../theme/tokens';
import { BTN_STANDARD } from '../theme/poButtonTokens';
import { AiImportBanner } from '../components/AiImportBanner';
import { setPendingBillImportFile } from '../utils/billImportHandoff';

const PAGE_MAX = 720;

/**
 * Standalone New Bill page — the AI import drop zone is the primary action.
 * Drop a supplier invoice → a draft bill is created, the file hands off to
 * the reconcile view (same handoff as the PO workspace), and AI parses the
 * lines. Optionally pick the supplier and a PO to link before dropping;
 * or start a blank bill for manual entry.
 */
export const BillNewPage: React.FC = () => {
  const navigate = useNavigate();
  const { suppliers, purchaseOrders, createBill } = useData();

  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');
  const [linkedPoId, setLinkedPoId] = useState<string | null>(null);

  const supplier = suppliers.find(s => s.id === supplierId);
  const poOptions = purchaseOrders
    .filter(p => !linkedPoId || p.id === linkedPoId || p.supplierId === supplierId)
    .map(p => p.id);

  const makeBill = () => {
    const po = linkedPoId ? purchaseOrders.find(p => p.id === linkedPoId) : null;
    return createBill({
      supplierId: po?.supplierId ?? supplierId,
      linkedPoId: linkedPoId,
      jobRef: po?.jobRef ?? '',
    });
  };

  const handleInvoiceDrop = (file: File) => {
    const newId = makeBill();
    setPendingBillImportFile(file);
    navigate(`/bills/${newId}?mode=reconcile`);
  };

  const handleBlankBill = () => {
    const newId = makeBill();
    navigate(`/bills/${newId}`);
  };

  return (
    <Box sx={{ maxWidth: PAGE_MAX, mx: 'auto', px: 2, py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Button startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/bills')}
          sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
            '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
          Back to Bills
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #e0d5f5', borderRadius: 2, p: 3, backgroundColor: '#fdfcff' }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" mb={2.5}>
          <ReceiptLongIcon sx={{ color: BRAND, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a1a2e' }}>
              New supplier bill
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#666', mt: 0.35 }}>
              Drop the supplier invoice below — AI reads the lines into a new bill.
              Pick the supplier (and a PO if you have one) first, or just drop the file.
            </Typography>
          </Box>
        </Stack>

        {/* Supplier + optional PO link */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2.5}>
          <TextField
            select size="small" label="Supplier" value={supplierId}
            onChange={e => { setSupplierId(e.target.value); setLinkedPoId(null); }}
            sx={{ minWidth: 240, flex: 1 }}
          >
            {suppliers.map(s => (
              <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.8rem' }}>{s.name}</MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            options={poOptions}
            value={linkedPoId}
            onChange={(_, v) => {
              setLinkedPoId(v);
              const po = v ? purchaseOrders.find(p => p.id === v) : null;
              if (po) setSupplierId(po.supplierId);
            }}
            renderInput={params => <TextField {...params} label="Link to PO (optional)" />}
            sx={{ minWidth: 220, flex: 1 }}
          />
        </Stack>

        <AiImportBanner
          variant="hero"
          onFile={handleInvoiceDrop}
          title="Supplier Invoice Import"
          subtitle={`Drag the supplier invoice here — AI reads the lines into a new ${supplier?.name ?? ''} bill${linkedPoId ? ` and opens reconciliation against ${linkedPoId}` : ''}`}
          buttonLabel="Choose invoice"
        />

        <Stack direction="row" spacing={1.5} alignItems="center" mt={2}>
          <Button size="small" variant="contained" disableElevation onClick={handleBlankBill}
            startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
            sx={{ ...BTN_STANDARD, textTransform: 'none' }}>
            + Start blank bill instead
          </Button>
          <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
            for freight, surcharges, or manual entry
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default BillNewPage;
